import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { toDependentCounts } from '@/components/deleteConfirmDialog';
import type { useArchiveBranchConfirm } from './useArchiveBranchConfirm';

type ArchiveBranchConfirmModalProps = {
    confirm: ReturnType<typeof useArchiveBranchConfirm>;
};

/**
 * Confirmation for taking a branch out of service.
 *
 * Render-only. Says the two things the click does that cannot be seen from the
 * menu item — the branch stops trading, and its terminal token is revoked — and
 * shows the server's refusal inline when a shift is still open on the branch.
 */
export const ArchiveBranchConfirmModal: React.FC<
    ArchiveBranchConfirmModalProps
> = ({ confirm }) => {
    const { t } = useTranslation('pages');
    const { target, error, isPending } = confirm;

    const blockers = toDependentCounts(error?.dependents);

    return (
        <Modal
            isOpen={target !== null}
            onOpenChange={(open) => !open && confirm.onCancel()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('branch-archive-confirm-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading
                                {...getNodeTestIdProp(
                                    'branch-archive-confirm-title',
                                )}
                            >
                                {t('branches.components.archiveConfirm.title')}
                            </Modal.Heading>
                            <p
                                className="text-sm text-muted"
                                {...getNodeTestIdProp(
                                    'branch-archive-confirm-prompt',
                                )}
                            >
                                {t(
                                    'branches.components.archiveConfirm.prompt',
                                    { name: target?.name ?? '' },
                                )}
                            </p>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            <p className="text-sm text-muted">
                                {t(
                                    'branches.components.archiveConfirm.description',
                                )}
                            </p>
                            <p
                                className="text-sm text-muted"
                                {...getNodeTestIdProp(
                                    'branch-archive-confirm-token-notice',
                                )}
                            >
                                {t(
                                    'branches.components.archiveConfirm.tokenRevoked',
                                )}
                            </p>

                            {error ? (
                                <div
                                    className="flex flex-col gap-3 rounded-xl bg-danger-soft p-4"
                                    {...getNodeTestIdProp(
                                        'branch-archive-confirm-error',
                                    )}
                                >
                                    <div className="flex items-start gap-2">
                                        <Icon
                                            aria-hidden
                                            className="mt-0.5 size-4 shrink-0 text-danger-soft-foreground"
                                            icon="gravity-ui:circle-exclamation"
                                        />
                                        <p
                                            className="text-sm text-danger-soft-foreground"
                                            {...getNodeTestIdProp(
                                                'branch-archive-confirm-error-message',
                                            )}
                                        >
                                            {error.message}
                                        </p>
                                    </div>
                                    {blockers.length > 0 ? (
                                        <ul
                                            className="flex flex-col gap-1 pl-6 text-sm text-danger-soft-foreground"
                                            {...getNodeTestIdProp(
                                                'branch-archive-confirm-dependents',
                                            )}
                                        >
                                            {blockers.map((blocker) => (
                                                <li
                                                    className="tabular-nums"
                                                    key={blocker.kind}
                                                >
                                                    {`${blocker.count} · ${t(
                                                        blocker.labelKey,
                                                        {
                                                            defaultValue:
                                                                blocker.kind,
                                                            ns: 'components',
                                                        },
                                                    )}`}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={confirm.onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'branch-archive-confirm-cancel',
                                )}
                            >
                                {t('branches.components.archiveConfirm.cancel')}
                            </Button>
                            <Button
                                isDisabled={isPending}
                                onPress={confirm.onConfirm}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'branch-archive-confirm-submit',
                                )}
                            >
                                {t('branches.components.archiveConfirm.submit')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
